import React, { useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import BoltIcon from "@mui/icons-material/Bolt";
import CalendarCheckIcon from "assets/images/icons/Calendar_Check.png";
import BusinessBag from "assets/images/icons/BusinessBag.png";
import Folder from "assets/images/icons/Folder.png";
import Goal from "assets/images/icons/Goal.png";
import Money from "assets/images/icons/Money.png";
import ProjectRecording from "assets/images/icons/ProjectRecording.png";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import data from "./data/index.js";
import deadlines from "./data/deadlines/index.js";
import events from "./data/events/index.js";
import eventsWaiting from "./data/eventsWaiting/index.js";
import permissions from "./data/permissions/index.js";
import { UserContext } from "context";

const Projects = ({ type }) => {
  const { user } = useContext(UserContext);
  const { userId } = useParams();
  const [pageIndex, setPageIndex] = useState(0);

  const navigate = useNavigate();
  // Fetch data
  const fetchedData = data();
  const fetchedDeadlines = deadlines(0, false);
  const fetchedEventsActive = events();
  const fetchedEventsEnded = events(true);
  const fetchedEventsValidation = events(false, true);
  const fetchedPermissions = permissions();
  const fetchedEventWaiting = eventsWaiting();

  // Loading state check
  const isLoading = [
    fetchedData.loading,
    fetchedDeadlines.loading,
    fetchedEventsActive.loading,
    fetchedEventsEnded.loading,
    fetchedEventsValidation.loading,
    fetchedPermissions.loading,
    fetchedEventWaiting.loading,
  ].some(Boolean);

  if (isLoading) return <div>Loading...</div>;

  // Define columns and rows based on `type`
  const dataMapping = {
    deadlines: { columns: fetchedDeadlines.columns, rows: fetchedDeadlines.rows },
    event: { columns: fetchedEventsActive.columns, rows: fetchedEventsActive.rows },
    eventArchive: { columns: fetchedEventsEnded.columns, rows: fetchedEventsEnded.rows },
    eventValidation: {
      columns: fetchedEventsValidation.columns,
      rows: fetchedEventsValidation.rows,
    },
    waitingForPayment: { columns: fetchedEventWaiting.columns, rows: fetchedEventWaiting.rows },
    permissions: { columns: fetchedPermissions.columns, rows: fetchedPermissions.rows },
    default: { columns: fetchedData.columns, rows: fetchedData.rows },
  };

  const { columns, rows } = dataMapping[type] || dataMapping.default;

  // Render header and button for specific types
  const headerTitles = {
    deadlines: "Scadenze",
    event: "Progetti attivi",
    eventArchive: "Archivio Eventi",
    eventValidation: "In Valutazione",
    permissions: "Permessi Ottenuti",
    waitingForPayment:"In attesa di pagamento"
  };

  const headerIcons = {
    deadlines: CalendarCheckIcon,
    event: BusinessBag,
    eventArchive: Folder,
    eventValidation: ProjectRecording,
    permissions: Goal,
    waitingForPayment:Money
  };
  const bgColor = {
    deadlines: "#000000",
    event: "#3A496A",
    eventArchive: "#280A2C",
    eventValidation: "#438E80",
    permissions: "#1E1E1E",
    waitingForPayment: "#B79d74",

  };

  const buttonNavigation = {
    deadlines: "/app/MyDeadlines",
    event: "/app/myEvents",
    eventArchive: "/app/myEvents",
    eventValidation: "/app/myEvents",
    permissions: "/app/myEvents",
    waitingForPayment: "/app/myEvents",
  };

  const buttonTexts = {
    deadlines: { icon: <BoltIcon />, text: "Vai Alle Scadenze" },
    event: { icon: <ArrowForwardIcon />, text: "Vai a Eventi" },
    eventArchive: { icon: <ArrowForwardIcon />, text: "Vai a Eventi" },
    eventValidation: { icon: <ArrowForwardIcon />, text: "Vai a Eventi" },
    permissions: { icon: <ArrowForwardIcon />, text: "Vai a Eventi" },
    waitingForPayment: { icon: <ArrowForwardIcon />, text: "Vai a Eventi" },
  };

  return (
    <Card>
      <MDBox mt={2}>
        {["deadlines", "event", "eventArchive", "eventValidation", "permissions","waitingForPayment"].includes(
          type
        ) && (
          <>
            {/* Title and Icon in the Same Line */}
            <MDBox
              mx={2}
              mt={-4}
              py={2}
              px={2}
              variant="gradient"
              sx={{
                backgroundColor: bgColor[type], // Use sx for custom bgColor
              }}
              borderRadius="lg"
              coloredShadow="info"
              display="flex" // Flex container for title and icon
              alignItems="center" // Vertically align title and icon
            >
              {/* Title */}
              <MDTypography variant="h4" color="white" sx={{ mr: 1 }}>
                {headerTitles[type]}
              </MDTypography>

              {/* Icon */}
              <MDBox
                component="img"
                src={headerIcons[type]}
                alt="NewRequest"
                width="24px"
                my={-4}
                sx={{
                  cursor: "pointer",
                }}
              />
            </MDBox>

            {/* Subtext */}
            <MDTypography mx={4} mt={1} component="p" variant="button" color="text">
              {type === "deadlines"
                ? "Clicca sulla freccia per andare al documento che richiede la tua attenzione"
                : type === "event"
                ? "Gli eventi futuri"
                : type === "eventArchive"
                ? "Eventi passati"
                : type === "eventValidation"
                ? "I progetti da valutare"
                : type === "permissions"
                ? "Clicca per scaricare"
                : ""}
            </MDTypography>

            {/* Data Table */}
            <MDBox my={2} mr={5}>
              <DataTable
                entriesPerPage={false}
                defaultPageSize={5}
                table={{ columns, rows }}
                showTotalEntries={false}
                isSorted={false}
                noEndBorder
                onPageChange={(newPageIndex) => setPageIndex(newPageIndex)} // Update state
              />
              <MDBox display="flex" justifyContent="flex-end" mt={1}>
                <MDButton
                  color="primary"
                    sx={{
                      borderRadius: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  onClick={() => navigate(buttonNavigation[type])}
                >
                  {buttonTexts[type].icon}
                  {buttonTexts[type].text}
                </MDButton>
              </MDBox>
            </MDBox>
          </>
        )}
        {type === "default" && (
          <DataTable
            entriesPerPage={false}
            defaultPageSize={5}
            table={{ columns, rows }}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
          />
        )}
      </MDBox>
    </Card>
  );
};

Projects.defaultProps = {
  type: "default",
};

Projects.propTypes = {
  type: PropTypes.oneOf([
    "default",
    "deadlines",
    "event",
    "eventArchive",
    "eventValidation",
    "permissions",
    "waitingForPayment",
  ]),
};

export default Projects;

